import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiSearch, FiChevronDown, FiMail, FiShield, FiX, FiMoreVertical, FiEdit, FiTrash2 } = FiIcons;

const initialMembers = [
  { id: 1, name: 'Alex Johnson', email: 'alex.j@example.com', role: 'Admin', status: 'Active', avatar: 'https://i.pravatar.cc/150?u=a042581f4e29026704d' },
  { id: 2, name: 'Maria Garcia', email: 'maria.g@example.com', role: 'Editor', status: 'Active', avatar: 'https://i.pravatar.cc/150?u=a042581f4e29026707d' },
  { id: 3, name: 'James Smith', email: 'james.s@example.com', role: 'Viewer', status: 'Pending', avatar: 'https://i.pravatar.cc/150?u=a042581f4e29026706d' },
  { id: 4, name: 'Patricia Brown', email: 'patricia.b@example.com', role: 'Editor', status: 'Inactive', avatar: 'https://i.pravatar.cc/150?u=a042581f4e29026705d' },
];

const statusStyles = {
  Active: 'bg-green-100 text-green-800',
  Pending: 'bg-yellow-100 text-yellow-800',
  Inactive: 'bg-red-100 text-red-800',
};

const TeamManagement = () => {
  const [members, setMembers] = useState(initialMembers);
  const [searchTerm, setSearchTerm] = useState('');
  const [isInviteModalOpen, setInviteModalOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  const filteredMembers = useMemo(() =>
    members.filter(member =>
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [members, searchTerm]
  );

  const InviteMemberModal = () => (
    <AnimatePresence>
      {isInviteModalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setInviteModalOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">Invite New Member</h3>
              <button onClick={() => setInviteModalOpen(false)} className="text-gray-400 hover:text-white transition-colors">
                <SafeIcon icon={FiX} className="h-6 w-6" />
              </button>
            </div>
            <form className="p-6 space-y-6">
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Email Address</label>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    placeholder="member@example.com"
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg pl-12 pr-4 py-3 focus:ring-2 focus:ring-yellow-500 focus:outline-none transition-all"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Assign Role</label>
                <div className="relative">
                  <SafeIcon icon={FiShield} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <select className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg pl-12 pr-4 py-3 appearance-none focus:ring-2 focus:ring-yellow-500 focus:outline-none transition-all">
                    <option>Admin</option>
                    <option>Editor</option>
                    <option>Viewer</option>
                  </select>
                  <SafeIcon icon={FiChevronDown} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="submit"
                  className="bg-yellow-500 text-gray-900 font-bold py-3 px-6 rounded-lg shadow-lg hover:bg-yellow-400 transition-colors flex items-center gap-2"
                >
                  <SafeIcon icon={FiPlus} />
                  Send Invitation
                </motion.button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
  
  const handleDropdown = (memberId) => {
    setOpenDropdown(openDropdown === memberId ? null : memberId);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 sm:p-6 lg:p-8">
      <InviteMemberModal />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto"
      >
        <header className="flex flex-col sm:flex-row justify-between sm:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Team Management</h1>
            <p className="text-gray-400 mt-1">Manage your team members and their roles.</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setInviteModalOpen(true)}
            className="bg-yellow-500 text-gray-900 font-bold py-2 px-4 rounded-lg shadow-md hover:bg-yellow-400 transition-colors flex items-center gap-2 mt-4 sm:mt-0"
          >
            <SafeIcon icon={FiPlus} />
            Invite Member
          </motion.button>
        </header>

        <div className="mb-6">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-12 pr-4 py-3 focus:ring-2 focus:ring-yellow-500 focus:outline-none transition-all"
            />
          </div>
        </div>

        <div className="bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-700/50">
                <tr>
                  <th className="p-4 font-semibold">Name</th>
                  <th className="p-4 font-semibold">Status</th>
                  <th className="p-4 font-semibold">Role</th>
                  <th className="p-4 font-semibold"></th>
                </tr>
              </thead>
              <AnimatePresence>
                <tbody>
                  {filteredMembers.map((member, index) => (
                    <motion.tr
                      key={member.id}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="border-b border-gray-700 hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="p-4 flex items-center gap-4">
                        <img src={member.avatar} alt={member.name} className="h-10 w-10 rounded-full object-cover" />
                        <div>
                          <p className="font-medium">{member.name}</p>
                          <p className="text-sm text-gray-400">{member.email}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusStyles[member.status]}`}>
                          {member.status}
                        </span>
                      </td>
                      <td className="p-4 text-gray-300">{member.role}</td>
                      <td className="p-4 text-right relative">
                        <button onClick={() => handleDropdown(member.id)} className="text-gray-400 hover:text-white p-2 rounded-full transition-colors">
                          <SafeIcon icon={FiMoreVertical} />
                        </button>
                        <AnimatePresence>
                        {openDropdown === member.id && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: -10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -10 }}
                            className="absolute right-0 mt-2 w-48 bg-gray-700 rounded-lg shadow-xl z-10 origin-top-right"
                          >
                            <a href="#" className="flex items-center gap-3 px-4 py-2 text-sm text-gray-200 hover:bg-gray-600 rounded-t-lg">
                              <SafeIcon icon={FiEdit} />
                              Edit Role
                            </a>
                            <a href="#" className="flex items-center gap-3 px-4 py-2 text-sm text-red-400 hover:bg-gray-600 rounded-b-lg">
                              <SafeIcon icon={FiTrash2} />
                              Remove Member
                            </a>
                          </motion.div>
                        )}
                        </AnimatePresence>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </AnimatePresence>
            </table>
          </div>
           {filteredMembers.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <p>No members found.</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default TeamManagement;